import React, { useEffect, useMemo, useState } from "react";
import { base44 } from "@/api/base44Client";
import MobileSelect from "@/components/drill/MobileSelect";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw, ChevronDown, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { isAtLeast } from "@/lib/roles";

export default function AdminAuditLogs() {
  const { effectiveRole } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [record, setRecord] = useState("");
  const [device, setDevice] = useState("");
  const [date, setDate] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    const [a, u] = await Promise.all([
      base44.entities.AuditLog.list("-occurred_at", 500),
      base44.entities.User.list(),
    ]);
    setLogs(a || []);
    setUsers(u || []);
  };

  useEffect(() => { void load(); }, []);

  const actions = useMemo(() => [...new Set(logs.map((l) => l.action).filter(Boolean))].sort(), [logs]);
  const devices = useMemo(() => [...new Set(logs.map((l) => l.device).filter(Boolean))].sort(), [logs]);
  const filtered = useMemo(() => logs.filter((l) => {
    if (userId && l.user_id !== userId) return false;
    if (action && l.action !== action) return false;
    if (record && !`${l.entity || ""} ${l.record_id || ""}`.toLowerCase().includes(record.toLowerCase())) return false;
    if (device && l.device !== device) return false;
    if (date && !(l.occurred_at || "").startsWith(date)) return false;
    return true;
  }), [logs, userId, action, record, device, date]);

  const clearHistory = async () => {
    if (!window.confirm("Clear all audit log history? This cannot be undone.")) return;
    setClearing(true);
    try {
      await base44.entities.AuditLog.deleteMany({});
      await load();
      setUserId(""); setAction(""); setRecord(""); setDevice(""); setDate("");
    } finally { setClearing(false); }
  };

  return (
    <section className="space-y-3">
      <div className="rounded-2xl border bg-white shadow-sm dark:bg-slate-900 dark:border-slate-800">
        <div className="flex items-center justify-between gap-3 p-3">
          <div><h2 className="font-semibold">Audit Logs</h2><p className="text-xs text-muted-foreground">Admin activity and data-change history.</p></div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-xl gap-2" onClick={clearHistory} disabled={clearing}><Trash2 className="w-4 h-4" />{clearing ? "Clearing..." : "Clear History"}</Button>
            <Button variant="ghost" size="icon" onClick={()=>void load()} aria-label="Refresh audit logs"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white shadow-sm dark:bg-slate-900 dark:border-slate-800">
        <button type="button" className="w-full flex items-center justify-between gap-3 p-3 text-left" onClick={()=>setFiltersOpen(v=>!v)} aria-expanded={filtersOpen}>
          <div><h3 className="font-semibold text-sm">Filters</h3><p className="text-xs text-muted-foreground">Filter the audit log list.</p></div>
          <ChevronDown className={`w-4 h-4 transition-transform ${filtersOpen?"rotate-180":""}`} />
        </button>
        {filtersOpen && <div className="px-3 pb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <MobileSelect value={userId || "__all__"} onValueChange={(v) => setUserId(v === "__all__" ? "" : v)} options={[{value:"__all__",label:"All users"},...users.map(u=>({value:u.id,label:u.full_name||u.name||u.email}))]} placeholder="User" />
          <MobileSelect value={action || "__all__"} onValueChange={(v) => setAction(v === "__all__" ? "" : v)} options={[{value:"__all__",label:"All actions"},...actions.map(v=>({value:v,label:v}))]} placeholder="Action" />
          <Input value={record} onChange={(e)=>setRecord(e.target.value)} placeholder="Record / ID" className="rounded-xl text-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" />
          <MobileSelect value={device || "__all__"} onValueChange={(v) => setDevice(v === "__all__" ? "" : v)} options={[{value:"__all__",label:"All devices"},...devices.map(v=>({value:v,label:v.slice(0,32)}))]} placeholder="Device" />
          <Input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="rounded-xl text-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" />
        </div>}
      </div>

      <div className="rounded-2xl border bg-white shadow-sm dark:bg-slate-900 dark:border-slate-800 p-3">
        <div className="max-h-[520px] overflow-y-auto space-y-1">
          {filtered.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No audit events match these filters.</p> : filtered.map((l) => (
            <div key={l.id} className="rounded-lg border px-2 py-1.5 text-sm dark:border-slate-800">
              <div className="flex flex-wrap justify-between gap-2"><span className="font-medium">{l.action}</span><span className="text-xs text-muted-foreground">{l.occurred_at ? new Date(l.occurred_at).toLocaleString() : "—"}</span></div>
              <div className="text-xs text-muted-foreground mt-0.5">{l.user_name || l.user_id || "Unknown"} · {l.entity || "—"} · {l.record_id || "—"}</div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">{l.device || "Unknown device"}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
